/**
 * Outbound Campaigns API
 * 
 * API functions for outbound campaign management
 */

import apiClient from './apiClient';

/**
 * Launch a new outbound campaign
 * 
 * @param {Object} campaignData - Campaign data
 * @param {string} campaignData.name - Campaign name
 * @param {string} campaignData.locationId - Location ID
 * @param {Array} campaignData.csvData - Array of CSV rows
 * @param {number} campaignData.totalAppointments - Total appointments count
 * @param {string} campaignData.draftId - Optional draft ID
 * @returns {Promise<Object>} Campaign object
 */
export const launchCampaign = async (campaignData) => {
  try {
    const response = await apiClient.post('/api/outbound/campaigns/launch', campaignData);
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to launch campaign');
    }
    
    return response.campaign;
  } catch (error) {
    console.error('Error launching campaign:', error);
    
    // Handle specific error codes
    if (error.response?.data?.error) {
      const apiError = error.response.data.error;
      
      switch (apiError.code) {
        case 'NO_PHONE_NUMBER':
          throw new Error('This location doesn\'t have a phone number configured. Please contact support.');
        case 'NO_AGENT_ID':
          throw new Error('This location doesn\'t have an AI agent configured. Please contact support.');
        case 'INVALID_CSV':
          const csvError = new Error('CSV validation failed');
          csvError.validationErrors = apiError.errors;
          throw csvError;
        case 'FORBIDDEN':
          throw new Error('You don\'t have permission to launch campaigns for this location.');
        default:
          throw new Error(apiError.message || 'Failed to launch campaign');
      }
    }
    
    throw error;
  }
};

/**
 * Get campaign details
 * 
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<Object>} Campaign object
 */
export const getCampaign = async (campaignId) => {
  try {
    const response = await apiClient.get(`/api/outbound/campaigns/${campaignId}`);
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch campaign');
    }
    
    return response.campaign;
  } catch (error) {
    console.error('Error fetching campaign:', error);
    throw error;
  }
};

/**
 * List all campaigns
 * 
 * @param {Object} params - Query parameters
 * @param {string} params.locationId - Optional location filter
 * @returns {Promise<Array>} Array of campaigns
 */
export const listCampaigns = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.locationId && params.locationId !== 'all') {
      queryParams.append('locationId', params.locationId);
    }
    
    const url = `/api/outbound/campaigns${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await apiClient.get(url);
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch campaigns');
    }
    
    return response.campaigns || [];
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

/**
 * Pause a campaign
 * 
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<Object>} Response
 */
export const pauseCampaign = async (campaignId) => {
  try {
    const response = await apiClient.post(`/api/outbound/campaigns/${campaignId}/pause`);
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to pause campaign');
    }
    
    return response;
  } catch (error) {
    console.error('Error pausing campaign:', error);
    throw error;
  }
};

/**
 * Resume a paused campaign
 * 
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<Object>} Response
 */
export const resumeCampaign = async (campaignId) => {
  try {
    const response = await apiClient.post(`/api/outbound/campaigns/${campaignId}/resume`);
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to resume campaign');
    }
    
    return response;
  } catch (error) {
    console.error('Error resuming campaign:', error);
    throw error;
  }
};

/**
 * Cancel a campaign
 * 
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<Object>} Response
 */
export const cancelCampaign = async (campaignId) => {
  try {
    const response = await apiClient.post(`/api/outbound/campaigns/${campaignId}/cancel`);
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to cancel campaign');
    }
    
    return response;
  } catch (error) {
    console.error('Error cancelling campaign:', error);
    throw error;
  }
};

/**
 * Save campaign as draft
 * 
 * @param {Object} draftData - Draft data
 * @returns {Promise<Object>} Draft object
 */
export const saveDraft = async (draftData) => {
  try {
    const response = await apiClient.post('/api/outbound/drafts', draftData);
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to save draft');
    }
    
    return response.draft;
  } catch (error) {
    console.error('Error saving draft:', error);
    throw error;
  }
};

/**
 * List all drafts
 * 
 * @param {Object} params - Query parameters
 * @param {string} params.locationId - Optional location filter
 * @returns {Promise<Array>} Array of drafts
 */
export const listDrafts = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.locationId && params.locationId !== 'all') {
      queryParams.append('locationId', params.locationId);
    }
    
    const url = `/api/outbound/drafts${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await apiClient.get(url);
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch drafts');
    }
    
    return response.drafts || [];
  } catch (error) {
    console.error('Error fetching drafts:', error);
    throw error;
  }
};

/**
 * Delete a draft
 * 
 * @param {string} draftId - Draft ID
 * @returns {Promise<Object>} Response
 */
export const deleteDraft = async (draftId) => {
  try {
    const response = await apiClient.delete(`/api/outbound/drafts/${draftId}`);
    
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete draft');
    }
    
    return response;
  } catch (error) {
    console.error('Error deleting draft:', error);
    throw error;
  }
};

const outboundApi = {
  launchCampaign,
  getCampaign,
  listCampaigns,
  pauseCampaign,
  resumeCampaign,
  cancelCampaign,
  saveDraft,
  listDrafts,
  deleteDraft,
};

export default outboundApi;

